import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertCircle,
  Calculator,
  CheckCircle,
  ClipboardList,
  FileText,
  Save,
  Sparkles,
  User,
  Brain,
  Loader2,
  ExternalLink,
  GraduationCap,
  BookOpen,
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatFieldValue = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (Array.isArray(value)) {
    if (value.length === 0) return '—';
    return value
      .map((item) => (typeof item === 'object' ? Object.values(item || {}).filter(Boolean).join(' • ') : item))
      .join(' | ');
  }
  if (typeof value === 'object') {
    const visibleValues = Object.values(value).filter(Boolean);
    return visibleValues.length ? visibleValues.join(' • ') : JSON.stringify(value);
  }
  return String(value);
};

const getRuleMaxScore = (rule = {}) => {
  if (rule?.scoring_method === 'yes_no') {
    return Math.max(Number(rule?.yes_value ?? 0), Number(rule?.no_value ?? 0));
  }

  return Number(rule?.max_score ?? 0);
};

const ApplicationScoring = ({
  application,
  scholarship,
  onScoreSubmit,
  readOnly = false,
}) => {
  const { user, impersonateUser } = useAuth();
  const [formData, setFormData] = useState(application?.form_data || {});

  useEffect(() => {
    setFormData(application?.form_data || {});
  }, [application]);
  const [scores, setScores] = useState({});
  const [suggestions, setSuggestions] = useState(null);
  const [existingScores, setExistingScores] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [expandedSections, setExpandedSections] = useState([]);
  
  // Transcript analysis state
  const [transcriptAnalyses, setTranscriptAnalyses] = useState({});
  const [analyzingTranscript, setAnalyzingTranscript] = useState({});
  const [selectedGpaScale, setSelectedGpaScale] = useState({});
  const [reuploadRequests, setReuploadRequests] = useState(application?.reupload_requests || []);
  const [deletingDraft, setDeletingDraft] = useState(false);

  useEffect(() => {
    setReuploadRequests(application?.reupload_requests || []);
  }, [application]);

  const canRequestReupload = ['admin', 'chair', 'co_chair', 'committee'].includes(user?.role);

  const getReuploadRequest = (fieldId) => {
    return (reuploadRequests || []).find(req => req.field_id === fieldId && req.status === 'requested');
  };

  const requestReupload = async (fieldId) => {
    if (!application?.id) return;
    const message = window.prompt('Optional message to applicant (leave blank for none):') ?? null;
    if (message === null) return;
    try {
      const response = await axios.post(`${API}/applications/${application.id}/request-reupload`, {
        field_id: fieldId,
        message: message.trim()
      });
      setReuploadRequests(response.data?.reupload_requests || []);
      toast.success('Reupload request sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to request reupload');
    }
  };

  const handleAdminFileUpload = async (fieldId, file) => {
    if (!file || !application?.id) return;
    const allowed = ['pdf', 'docx', 'xls', 'jpg', 'jpeg', 'png', 'csv'];
    const ext = file.name.split('.').pop()?.toLowerCase() || '';
    if (!allowed.includes(ext)) {
      toast.error('Unsupported file type');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File exceeds 5MB size limit');
      return;
    }
    try {
      const payload = new FormData();
      payload.append('file', file);
      payload.append('field_id', fieldId);
      payload.append('display_name', file.name);
      const response = await axios.post(`${API}/applications/${application.id}/files`, payload, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      const uploadedFile = response.data?.file;
      if (uploadedFile) {
        setFormData(prev => ({ ...prev, [fieldId]: uploadedFile }));
        toast.success('File uploaded for applicant');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    }
  };

  const deleteDraftReview = async () => {
    if (!application?.id || !myExistingScore || myExistingScore.is_complete) return;
    const confirmed = window.confirm('Remove your draft review? This cannot be undone.');
    if (!confirmed) return;
    setDeletingDraft(true);
    try {
      await axios.delete(`${API}/applications/${application.id}/scores/draft`);
      toast.success('Draft review removed');
      await fetchScoresAndSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove draft review');
    } finally {
      setDeletingDraft(false);
    }
  };

  const deleteDraftReviewForReviewer = async (reviewerId, reviewerName) => {
    if (!application?.id || !reviewerId) return;
    const confirmed = window.confirm(`Remove ${reviewerName || 'this reviewer'}'s draft review? This cannot be undone.`);
    if (!confirmed) return;
    setDeletingDraft(true);
    try {
      await axios.delete(`${API}/applications/${application.id}/scores/draft`, {
        params: { reviewer_id: reviewerId }
      });
      toast.success('Draft review removed');
      await fetchScoresAndSuggestions();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove draft review');
    } finally {
      setDeletingDraft(false);
    }
  };

  const handleImpersonateApplicant = async () => {
    if (!application?.applicant_id) {
      toast.error('Applicant information is missing');
      return;
    }
    const confirmed = window.confirm(`Impersonate ${application.applicant_name || 'this applicant'}?`);
    if (!confirmed) return;
    try {
      await impersonateUser(application.applicant_id);
      window.location.href = `/application/${application.scholarship_id}/${application.id}`;
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to impersonate applicant');
    }
  };

  const scoringTemplate = scholarship?.scoring_template || { sections: [] };

  useEffect(() => {
    setExpandedSections((scoringTemplate.sections || []).map((section) => section.section_id));
  }, [scoringTemplate]);

  useEffect(() => {
    fetchScoresAndSuggestions();
  }, [application?.id, user?.id]);

  const myExistingScore = useMemo(
    () => existingScores?.reviewer_scores?.find((reviewerScore) => reviewerScore.reviewer_id === user?.id) || null,
    [existingScores, user?.id]
  );

  const fetchScoresAndSuggestions = async () => {
    if (!application?.id) return;

    setLoading(true);
    try {
      const [scoresRes, suggestionsRes] = await Promise.all([
        axios.get(`${API}/applications/${application.id}/scores`),
        axios.get(`${API}/applications/${application.id}/scores/auto-suggest`),
      ]);

      setExistingScores(scoresRes.data);
      setSuggestions(suggestionsRes.data);

      const initialScores = {};
      const myScores = scoresRes.data.reviewer_scores?.find((reviewerScore) => reviewerScore.reviewer_id === user?.id);

      if (myScores) {
        myScores.section_scores.forEach((section) => {
          section.field_scores.forEach((fieldScore) => {
            initialScores[fieldScore.field_id] = {
              score: fieldScore.score,
              notes: fieldScore.notes || '',
              max_score: fieldScore.max_score,
              auto_suggested: fieldScore.auto_suggested || false,
            };
          });
        });
      } else {
        suggestionsRes.data.suggested_scores?.forEach((section) => {
          section.field_scores.forEach((fieldScore) => {
            initialScores[fieldScore.field_id] = {
              score: fieldScore.auto_suggested ? fieldScore.suggested_score : null,
              notes: '',
              max_score: fieldScore.max_score,
              auto_suggested: fieldScore.auto_suggested,
            };
          });
        });
      }

      setScores(initialScores);
    } catch (error) {
      console.error('Failed to fetch scores:', error);
      toast.error('Failed to load scoring data');
    } finally {
      setLoading(false);
    }
  };

  const updateScore = (fieldId, value, maxScore) => {
    if (value === '') {
      setScores((previousScores) => ({
        ...previousScores,
        [fieldId]: { ...previousScores[fieldId], score: null },
      }));
      return;
    }

    const numValue = parseFloat(value);
    if (isNaN(numValue)) return;

    if (numValue < 0) {
      setScores((previousScores) => ({ ...previousScores, [fieldId]: { ...previousScores[fieldId], score: 0 } }));
    } else if (numValue > maxScore) {
      setScores((previousScores) => ({ ...previousScores, [fieldId]: { ...previousScores[fieldId], score: maxScore } }));
    } else {
      setScores((previousScores) => ({ ...previousScores, [fieldId]: { ...previousScores[fieldId], score: numValue } }));
    }
  };

  const updateNotes = (fieldId, notes) => {
    setScores((previousScores) => ({ ...previousScores, [fieldId]: { ...previousScores[fieldId], notes } }));
  };

  const applySuggestion = (fieldId, suggestedScore, maxScore) => {
    setScores((previousScores) => ({
      ...previousScores,
      [fieldId]: {
        ...previousScores[fieldId],
        score: suggestedScore,
        max_score: maxScore,
        auto_suggested: true,
      },
    }));
    toast.success('Applied suggested score');
  };

  const calculateSectionTotal = (sectionConfig) => {
    let total = 0;
    let max = 0;

    sectionConfig.field_rules.forEach((rule) => {
      const fieldScore = scores[rule.field_id];
      if (fieldScore && fieldScore.score !== null) {
        total += Number(fieldScore.score);
      }
      max += getRuleMaxScore(rule);
    });

    return { total, max };
  };

  const calculateOverallTotal = () => {
    let total = 0;
    let max = 0;

    scoringTemplate.sections.forEach((section) => {
      const sectionTotals = calculateSectionTotal(section);
      total += sectionTotals.total;
      max += sectionTotals.max;
    });

    return { total, max };
  };

  const getSuggestionForField = (fieldId) => {
    if (!suggestions) return null;

    for (const section of suggestions.suggested_scores || []) {
      const fieldScore = section.field_scores.find((item) => item.field_id === fieldId);
      if (fieldScore && fieldScore.auto_suggested) {
        return fieldScore;
      }
    }
    return null;
  };

  // Analyze transcript with AI
  const analyzeTranscript = async (fieldId, fileMeta) => {
    setAnalyzingTranscript(prev => ({ ...prev, [fieldId]: true }));
    
    try {
      const payload = { field_id: fieldId };
      if (fileMeta?.file_id) {
        payload.file_id = fileMeta.file_id;
      } else if (fileMeta?.url) {
        payload.file_url = fileMeta.url;
      } else {
        throw new Error('Transcript file is missing');
      }

      const response = await axios.post(`${API}/applications/${application.id}/analyze-transcript`, payload);
      
      setTranscriptAnalyses(prev => ({ ...prev, [fieldId]: response.data }));
      toast.success('Transcript analyzed successfully');
    } catch (error) {
      console.error('Transcript analysis failed:', error);
      toast.error(error.response?.data?.detail || error.message || 'Failed to analyze transcript');
    } finally {
      setAnalyzingTranscript(prev => ({ ...prev, [fieldId]: false }));
    }
  };

  // Check if a field is a transcript/file upload
  const isTranscriptField = (field) => {
    if (!field) return false;
    const label = (field.label || '').toLowerCase();
    return field.type === 'file' && (
      label.includes('transcript') ||
      label.includes('academic record') ||
      label.includes('grade report')
    );
  };

  // Get file metadata from form data
  const getFileMeta = (fieldValue) => {
    if (!fieldValue) return null;
    if (typeof fieldValue === 'object' && fieldValue.file_id) return fieldValue;
    if (typeof fieldValue === 'object' && fieldValue.url) {
      return { ...fieldValue, url: fieldValue.url };
    }
    if (typeof fieldValue === 'string' && fieldValue.startsWith('http')) {
      return { url: fieldValue, name: fieldValue.split('/').pop() };
    }
    if (typeof fieldValue === 'string') {
      return { name: fieldValue };
    }
    return null;
  };

  const openFile = async (fileMeta) => {
    if (!fileMeta) return;
    if (fileMeta.url && !fileMeta.file_id) {
      window.open(fileMeta.url, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!fileMeta.file_id) {
      toast.error('File is not available');
      return;
    }
    try {
      const authToken = localStorage.getItem('token');
      const response = await fetch(`${API}/files/${fileMeta.file_id}`, {
        headers: authToken ? { Authorization: `Bearer ${authToken}` } : {}
      });
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        toast.error(errorData?.detail || 'Failed to open file');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileMeta.name || 'document');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to open file');
    }
  };

  // Render transcript analysis results
  const TranscriptAnalysisDisplay = ({ analysis, fieldId }) => {
    const scale = selectedGpaScale[fieldId] || 'actual';
    
    const getDisplayGpa = () => {
      if (!analysis?.cumulative_gpa) return '—';
      
      if (scale === 'actual') {
        return `${analysis.cumulative_gpa} / ${analysis.gpa_scale || '?'}`;
      }
      
      if (analysis.normalized_gpa) {
        const normalized = analysis.normalized_gpa[`scale_${scale}`];
        return normalized ? `${normalized} / ${scale}` : '—';
      }
      
      return `${analysis.cumulative_gpa} / ${analysis.gpa_scale || '?'}`;
    };

    return (
      <div className="mt-3 rounded-md border border-blue-200 bg-blue-50 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h4 className="flex items-center gap-2 font-medium text-blue-900">
            <GraduationCap className="h-4 w-4" />
            AI Transcript Analysis
          </h4>
          <Select
            value={scale}
            onValueChange={(value) => setSelectedGpaScale(prev => ({ ...prev, [fieldId]: value }))}
          >
            <SelectTrigger className="w-40 h-8 text-xs">
              <SelectValue placeholder="GPA Scale" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="actual">Actual Scale</SelectItem>
              <SelectItem value="4">Normalize to 4.0</SelectItem>
              <SelectItem value="10">Normalize to 10.0</SelectItem>
              <SelectItem value="12">Normalize to 12.0</SelectItem>
            </SelectContent>
          </Select>
        </div>
        
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-4">
          <div className="rounded bg-white p-2">
            <p className="text-xs text-slate-500">Student</p>
            <p className="font-medium text-slate-900">{analysis.student_name || '—'}</p>
          </div>
          <div className="rounded bg-white p-2">
            <p className="text-xs text-slate-500">Institution</p>
            <p className="font-medium text-slate-900">{analysis.institution || '—'}</p>
          </div>
          <div className="rounded bg-white p-2">
            <p className="text-xs text-slate-500">Cumulative GPA</p>
            <p className="font-medium text-blue-700">{getDisplayGpa()}</p>
          </div>
          <div className="rounded bg-white p-2">
            <p className="text-xs text-slate-500">Total Credits</p>
            <p className="font-medium text-slate-900">{analysis.total_credits || '—'}</p>
          </div>
        </div>
        
        {analysis.semesters && analysis.semesters.length > 0 && (
          <div className="mt-3">
            <p className="mb-2 text-xs font-medium text-blue-800">Semester Breakdown</p>
            <div className="max-h-48 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-blue-100">
                  <tr>
                    <th className="p-2 text-left">Term</th>
                    <th className="p-2 text-center">Courses</th>
                    <th className="p-2 text-center">Credits</th>
                    <th className="p-2 text-center">GPA</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.semesters.map((sem, idx) => (
                    <tr key={idx} className="border-b border-blue-100 bg-white">
                      <td className="p-2">{sem.term}</td>
                      <td className="p-2 text-center">{sem.courses?.length || 0}</td>
                      <td className="p-2 text-center">{sem.semester_credits || '—'}</td>
                      <td className="p-2 text-center font-medium">{sem.semester_gpa || '—'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        <p className="mt-2 text-xs text-blue-600">
          Analyzed: {new Date(analysis.analyzed_at).toLocaleString()}
        </p>
      </div>
    );
  };

  const handleSubmit = async (isComplete = false) => {
    setSubmitting(true);

    try {
      const sectionScores = scoringTemplate.sections.map((section) => {
        const fieldScores = section.field_rules.map((rule) => ({
          field_id: rule.field_id,
          score: scores[rule.field_id]?.score ?? 0,
          max_score: getRuleMaxScore(rule),
          auto_suggested: scores[rule.field_id]?.auto_suggested || false,
          notes: scores[rule.field_id]?.notes || '',
        }));

        const totals = calculateSectionTotal(section);

        return {
          section_id: section.section_id,
          field_scores: fieldScores,
          total_score: totals.total,
          max_score: totals.max,
        };
      });

      await axios.post(`${API}/applications/${application.id}/scores`, {
        section_scores: sectionScores,
        is_complete: isComplete,
      });

      toast.success(isComplete ? 'Score submitted successfully' : 'Score saved as draft');
      await fetchScoresAndSuggestions();

      if (onScoreSubmit) {
        onScoreSubmit({
          totals: calculateOverallTotal(),
          isComplete,
        });
      }
    } catch (error) {
      console.error('Failed to submit score:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit score');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12" data-testid="application-scoring-loading-state">
        <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-greek-blue"></div>
      </div>
    );
  }

  if (!scoringTemplate.sections || scoringTemplate.sections.length === 0) {
    return (
      <div className="py-8 text-center text-slate-500" data-testid="application-scoring-empty-state">
        <AlertCircle className="mx-auto mb-4 h-12 w-12 opacity-50" />
        <p>No scoring template configured for this scholarship.</p>
        <p className="mt-2 text-sm">Ask a committee chair to set up the scoring template.</p>
      </div>
    );
  }

  const overallTotals = calculateOverallTotal();

  return (
    <div className="space-y-6" data-testid="application-scoring-panel">
      <div className="flex flex-col gap-4 rounded-lg border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between">
        <div>
          <h3 className="font-heading text-lg font-semibold text-slate-900">Score Application</h3>
          <p className="text-sm text-slate-500" data-testid="application-scoring-applicant-name">
            Applicant: <strong>{application.applicant_name || 'Unknown'}</strong>
          </p>
          <p className="text-xs text-slate-400" data-testid="application-scoring-scholarship-name">
            Scholarship: {application.scholarship_name || scholarship?.name || 'Unknown'}
          </p>
          {['admin', 'chair', 'co_chair'].includes(user?.role) && (
            <Button
              variant="outline"
              size="sm"
              onClick={handleImpersonateApplicant}
              className="mt-3 border-blue-300 text-blue-600 hover:bg-blue-50"
              data-testid="impersonate-applicant-btn"
            >
              Impersonate Applicant
            </Button>
          )}
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div className="rounded-md bg-slate-50 px-4 py-3 text-right" data-testid="application-scoring-total-card">
            <p className="text-3xl font-bold text-greek-blue">
              {overallTotals.total.toFixed(1)} <span className="text-lg text-slate-400">/ {overallTotals.max}</span>
            </p>
            <p className="text-sm text-slate-500">Current reviewer total</p>
          </div>
          <div className="rounded-md bg-blue-50 px-4 py-3 text-right" data-testid="application-scoring-average-card">
            <p className="text-2xl font-semibold text-blue-900">
              {existingScores?.score_summary?.average_score ?? '—'}
              {existingScores?.score_summary?.max_possible_score ? (
                <span className="text-base text-blue-700"> / {existingScores.score_summary.max_possible_score}</span>
              ) : null}
            </p>
            <p className="text-sm text-blue-700">Review average</p>
          </div>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="application-scoring-reviewer-status">
          <div className="flex items-center gap-2 text-slate-900">
            <ClipboardList className="h-4 w-4 text-greek-blue" />
            <span className="font-medium">Review progress</span>
          </div>
          <div className="mt-3 space-y-1 text-sm text-slate-600">
            <p data-testid="application-scoring-complete-reviewers">Completed reviewers: {existingScores?.completed_reviewers || 0}</p>
            <p data-testid="application-scoring-draft-reviewers">Draft reviewers: {existingScores?.draft_reviewers || 0}</p>
            <p data-testid="application-scoring-total-reviewers">Total reviewers: {existingScores?.total_reviewers || 0}</p>
          </div>
        </div>
        <div className="rounded-lg border border-slate-200 bg-white p-4 md:col-span-2" data-testid="application-scoring-my-status">
          <div className="flex items-center gap-2 text-slate-900">
            <User className="h-4 w-4 text-greek-blue" />
            <span className="font-medium">Your review</span>
          </div>
          <p className="mt-3 text-sm text-slate-600">
            {myExistingScore
              ? myExistingScore.is_complete
                ? 'You already submitted a completed review. You can update it if needed.'
                : 'You have a saved draft review for this application.'
              : 'No saved review yet. Suggested scores are prefilled where automatic scoring is available.'}
          </p>
          {myExistingScore && !myExistingScore.is_complete && (
            <div className="mt-3">
              <Button
                variant="outline"
                size="sm"
                onClick={deleteDraftReview}
                disabled={deletingDraft}
                className="border-red-200 text-red-600 hover:bg-red-50"
                data-testid="delete-draft-review-btn"
              >
                {deletingDraft ? 'Removing...' : 'Clear Draft Review'}
              </Button>
            </div>
          )}
        </div>
      </div>

      {existingScores && existingScores.reviewer_scores.length > 0 && (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4" data-testid="application-scoring-review-activity">
          <h4 className="mb-2 flex items-center gap-2 font-medium text-blue-900">
            <User className="h-4 w-4" />
            Review activity
          </h4>
          <div className="flex flex-wrap gap-3">
            {existingScores.reviewer_scores.map((reviewerScore, index) => (
              <div
                key={`${reviewerScore.reviewer_id}-${index}`}
                className="rounded bg-white px-3 py-2 text-sm"
                data-testid={`application-scoring-reviewer-chip-${index}`}
              >
                <span className="text-slate-600">{reviewerScore.reviewer_name}:</span>
                <span className="ml-1 font-semibold text-slate-900">
                  {reviewerScore.total_score.toFixed(1)}/{reviewerScore.max_possible_score}
                </span>
                <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${reviewerScore.is_complete ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                  {reviewerScore.is_complete ? 'Complete' : 'Draft'}
                </span>
                {user?.role === 'admin' && !reviewerScore.is_complete && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => deleteDraftReviewForReviewer(reviewerScore.reviewer_id, reviewerScore.reviewer_name)}
                    disabled={deletingDraft}
                    className="ml-2 text-red-600 hover:text-red-700"
                    data-testid={`admin-clear-draft-${reviewerScore.reviewer_id}`}
                  >
                    Clear Draft
                  </Button>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      <Accordion type="multiple" value={expandedSections} onValueChange={setExpandedSections}>
        {scoringTemplate.sections.map((section) => {
          const sectionTotals = calculateSectionTotal(section);
          const sectionSuggestion = suggestions?.suggested_scores?.find((item) => item.section_id === section.section_id);

          return (
            <AccordionItem
              key={section.section_id}
              value={section.section_id}
              className="mb-2 rounded-sm border bg-white"
              data-testid={`application-scoring-section-${section.section_id}`}
            >
              <AccordionTrigger className="px-4 py-3 hover:no-underline">
                <div className="flex w-full items-center justify-between gap-4 pr-4">
                  <div className="flex items-center gap-3">
                    <Calculator className="h-5 w-5 text-greek-blue" />
                    <span className="font-medium text-slate-900">{section.section_title}</span>
                  </div>
                  <div className="text-right">
                    <span className={`font-semibold ${sectionTotals.total === sectionTotals.max ? 'text-emerald-600' : 'text-slate-700'}`}>
                      {sectionTotals.total.toFixed(1)} / {sectionTotals.max}
                    </span>
                    {sectionSuggestion?.suggested_total !== undefined ? (
                      <p className="text-xs text-slate-500">Suggested: {sectionSuggestion.suggested_total}</p>
                    ) : null}
                  </div>
                </div>
              </AccordionTrigger>
              <AccordionContent className="px-4 pb-4">
                <div className="space-y-4">
                  {/* Get all fields from the form section */}
                  {(() => {
                    const formSection = scholarship?.form_config?.find((item) => item.id === section.section_id);
                    const allFields = formSection?.fields || [];
                    
                    // Create a map of field_id to scoring rule for quick lookup
                    const scoringRulesMap = {};
                    section.field_rules.forEach(rule => {
                      scoringRulesMap[rule.field_id] = rule;
                    });
                    
                    return allFields.map((field, fieldIndex) => {
                      const rule = scoringRulesMap[field.id];
                      const hasScoring = !!rule && getRuleMaxScore(rule) > 0;
                      const fieldScore = rule ? (scores[rule.field_id] || { score: null, notes: '' }) : { score: null, notes: '' };
                      const suggestion = rule ? getSuggestionForField(rule.field_id) : null;
                      const rawFieldValue = formData[field.id];
                      const fieldValue = formatFieldValue(rawFieldValue);
                      const fieldLabel = field.label || field.id;
                      const fieldType = field.type || 'text';
                      const isTranscript = isTranscriptField(field);
                      const fileMeta = getFileMeta(rawFieldValue);
                      const transcriptAnalysis = transcriptAnalyses[field.id];

                      return (
                        <div
                          key={`${field.id}-${fieldIndex}`}
                          className={`rounded-sm border p-4 ${hasScoring ? 'border-slate-200 bg-slate-50' : 'border-slate-100 bg-white'}`}
                          data-testid={`application-scoring-field-${field.id}`}
                        >
                          {/* Field Header with Question */}
                          <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                            <div className="flex-1">
                              <div className="flex items-center gap-2">
                                <Label className="text-sm font-medium text-slate-900" data-testid={`application-scoring-field-label-${field.id}`}>
                                  {fieldLabel}
                                </Label>
                                {!hasScoring && (
                                  <span className="text-xs px-2 py-0.5 rounded bg-slate-100 text-slate-500">
                                    Reference Only
                                  </span>
                                )}
                              </div>
                              
                              {/* Enhanced Applicant Response Display */}
                              <div className="mt-2 rounded-md border border-slate-200 bg-white p-3">
                                <div className="flex items-start gap-2 text-sm">
                                  <BookOpen className="mt-0.5 h-4 w-4 text-greek-blue flex-shrink-0" />
                                  <div className="flex-1">
                                    <p className="text-xs font-medium text-greek-blue mb-1">Applicant's Response:</p>
                                    {fieldType === 'file' ? (
                                      fileMeta ? (
                                        <div className="flex flex-wrap items-center gap-2">
                                          {(fileMeta.file_id || fileMeta.url) && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => openFile(fileMeta)}
                                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                              data-testid={`application-scoring-file-${field.id}`}
                                            >
                                              <ExternalLink className="h-3 w-3 mr-1" />
                                              View Uploaded File
                                            </Button>
                                          )}
                                          <span className="text-xs text-slate-500">{fileMeta.name || fileMeta.original_name || 'Uploaded file'}</span>
                                          {!fileMeta.file_id && !fileMeta.url && (
                                            <span className="text-xs text-amber-600">File upload storage not available for this record</span>
                                          )}
                                          {isTranscript && !readOnly && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => analyzeTranscript(field.id, fileMeta)}
                                              disabled={analyzingTranscript[field.id]}
                                              className="text-blue-600 border-blue-300 hover:bg-blue-50"
                                            >
                                              {analyzingTranscript[field.id] ? (
                                                <>
                                                  <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                                                  Analyzing...
                                                </>
                                              ) : (
                                                <>
                                                  <Brain className="mr-1 h-3 w-3" />
                                                  {transcriptAnalysis ? 'Re-Analyze' : 'Analyze Transcript'}
                                                </>
                                              )}
                                            </Button>
                                          )}
                                          {canRequestReupload && (
                                            <Button
                                              variant="outline"
                                              size="sm"
                                              onClick={() => requestReupload(field.id)}
                                              className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                              data-testid={`request-reupload-${field.id}`}
                                            >
                                              Request Re-upload
                                            </Button>
                                          )}
                                          {user?.role === 'admin' && (
                                            <div className="inline-flex items-center">
                                              <input
                                                id={`admin-file-input-${field.id}`}
                                                type="file"
                                                className="hidden"
                                                onChange={(event) => {
                                                  const selectedFile = event.target.files?.[0];
                                                  if (selectedFile) {
                                                    handleAdminFileUpload(field.id, selectedFile);
                                                  }
                                                  event.target.value = '';
                                                }}
                                                accept=".pdf,.docx,.xls,.csv,.jpg,.jpeg,.png"
                                                data-testid={`admin-upload-file-${field.id}`}
                                              />
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => document.getElementById(`admin-file-input-${field.id}`)?.click()}
                                                className="text-green-700 border-green-300 hover:bg-green-50"
                                              >
                                                Upload for Applicant
                                              </Button>
                                            </div>
                                          )}
                                          {getReuploadRequest(field.id) && (
                                            <span className="text-xs text-amber-600">Re-upload requested</span>
                                          )}
                                        </div>
                                      ) : (
                                        <div className="flex flex-wrap items-center gap-2">
                                          <span className="italic text-slate-400">No file uploaded</span>
                                          {user?.role === 'admin' && (
                                            <div className="inline-flex items-center">
                                              <input
                                                id={`admin-file-input-${field.id}`}
                                                type="file"
                                                className="hidden"
                                                onChange={(event) => {
                                                  const selectedFile = event.target.files?.[0];
                                                  if (selectedFile) {
                                                    handleAdminFileUpload(field.id, selectedFile);
                                                  }
                                                  event.target.value = '';
                                                }}
                                                accept=".pdf,.docx,.xls,.csv,.jpg,.jpeg,.png"
                                                data-testid={`admin-upload-file-${field.id}`}
                                              />
                                              <Button
                                                type="button"
                                                variant="outline"
                                                size="sm"
                                                onClick={() => document.getElementById(`admin-file-input-${field.id}`)?.click()}
                                                className="text-green-700 border-green-300 hover:bg-green-50"
                                              >
                                                Upload for Applicant
                                              </Button>
                                            </div>
                                          )}
                                        </div>
                                      )
                                    ) : fieldType === 'textarea' || (fieldValue && fieldValue.length > 100) ? (
                                      <div className="text-slate-800 whitespace-pre-wrap bg-slate-50 p-2 rounded text-sm max-h-40 overflow-y-auto">
                                        {fieldValue || <span className="italic text-slate-400">No response provided</span>}
                                      </div>
                                    ) : (
                                      <span className="font-medium text-slate-800" data-testid={`application-scoring-field-value-${field.id}`}>
                                        {fieldValue || <span className="italic text-slate-400">No response provided</span>}
                                      </span>
                                    )}
                                  </div>
                                </div>
                              </div>
                              
                              {/* Transcript Analysis Results */}
                              {isTranscript && transcriptAnalysis && (
                                <TranscriptAnalysisDisplay analysis={transcriptAnalysis} fieldId={field.id} />
                              )}
                              
                              {rule?.description && (
                                <p className="mt-2 text-xs italic text-slate-500 bg-amber-50 p-2 rounded border border-amber-100" data-testid={`application-scoring-guidance-${field.id}`}>
                                  <span className="font-medium text-amber-700">Scoring Guidance:</span> {rule.description}
                                </p>
                              )}
                            </div>
                            {hasScoring && (
                              <div className="text-right text-sm text-slate-500" data-testid={`application-scoring-max-score-${field.id}`}>
                                Max: {getRuleMaxScore(rule)} pts
                              </div>
                            )}
                          </div>

                          {/* Scoring Controls - Only show for fields with scoring rules */}
                          {hasScoring && (
                            <>
                              <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between border-t border-slate-200 pt-3 mt-3">
                                <div className="flex items-center gap-2">
                                  <Input
                                    type="number"
                                    min="0"
                                    max={getRuleMaxScore(rule)}
                                    step="0.5"
                                    value={fieldScore.score ?? ''}
                                    onChange={(event) => updateScore(rule.field_id, event.target.value, getRuleMaxScore(rule))}
                                    disabled={readOnly}
                                    className="w-28 rounded-sm"
                                    placeholder="Score"
                                    data-testid={`application-scoring-score-input-${field.id}`}
                                  />
                                  <span className="text-slate-500">/ {getRuleMaxScore(rule)}</span>
                                  {suggestion && suggestion.suggested_score !== null && !readOnly && (
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => applySuggestion(rule.field_id, suggestion.suggested_score, getRuleMaxScore(rule))}
                                      className="text-amber-600 hover:bg-amber-50 hover:text-amber-700"
                                      title="Apply auto-suggested score"
                                      data-testid={`application-scoring-apply-suggestion-${field.id}`}
                                    >
                                      <Sparkles className="mr-1 h-4 w-4" />
                                      Suggest: {suggestion.suggested_score}
                                    </Button>
                                  )}
                                </div>

                                <div className="rounded-md bg-white px-3 py-2 text-xs text-slate-500" data-testid={`application-scoring-method-${field.id}`}>
                                  Method: <span className="font-medium text-slate-700">{rule.scoring_method?.replace('_', ' ')}</span>
                                </div>
                              </div>

                              {!readOnly && (
                                <div className="mt-3">
                                  <Input
                                    value={fieldScore.notes || ''}
                                    onChange={(event) => updateNotes(rule.field_id, event.target.value)}
                                    placeholder="Add reviewer notes (optional)"
                                    className="rounded-sm text-sm"
                                    data-testid={`application-scoring-notes-input-${field.id}`}
                                  />
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      );
                    });
                  })()}
                </div>
              </AccordionContent>
            </AccordionItem>
          );
        })}
      </Accordion>

      {!readOnly && (
        <div
          className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 md:flex-row md:items-center md:justify-between"
          data-testid="application-scoring-actions"
        >
          <div className="text-sm text-slate-500">
            Save your progress as a draft, or submit your completed review.
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              onClick={() => handleSubmit(false)}
              disabled={submitting}
              className="rounded-sm"
              data-testid="application-scoring-save-draft-button"
            >
              <Save className="mr-2 h-4 w-4" />
              Save Draft
            </Button>
            <Button
              onClick={() => handleSubmit(true)}
              disabled={submitting}
              className="rounded-sm bg-greek-blue hover:bg-greek-blue-dark"
              data-testid="application-scoring-submit-button"
            >
              <CheckCircle className="mr-2 h-4 w-4" />
              Submit Score
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ApplicationScoring;
